import { createFileRoute, useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
    AlertCircle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Building,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Sparkles,
    ThumbsDown,
    ThumbsUp,
    UserCheck,
} from 'lucide-react';
import {
    type ComponentType,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { DataTable } from '@apps/web/components/ui/data_table';
import {
    type CustomFilterConfig,
    DataTableToolbar,
} from '@apps/web/components/ui/data_table_toolbar';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { getApiClient } from '@apps/web/lib/api_client';
import {
    areArraysEqual,
    areColumnFiltersEqual,
    extractExcludeFilterValues,
    extractFilterValues,
    normalizeValues,
} from '@apps/web/lib/column_filter_utils';
import { formatRelativeTimeShort } from '@apps/web/lib/format_relative_time';
import {
    type ColumnDef,
    type ColumnFiltersState,
    type RowData,
    type Table as TableType,
} from '@apps/web/lib/table';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import { cn } from '@apps/web/lib/utils';
import {
    booleanColumnFilterValue,
    buildInlineReviewStatsQueryParams,
    extractBooleanColumnFilter,
} from '@apps/web/routes/_app/admin/sms-engine-runs/filter_utils';
import {
    ACTION_TYPE_OPTIONS,
    DOMAIN_TYPE_OPTIONS,
    INLINE_REVIEW_MODE_OPTIONS,
    INLINE_REVIEW_OUTCOME_OPTIONS,
    INLINE_REVIEW_VERDICT_OPTIONS,
    MODE_OPTIONS,
    PER_PAGE,
    REVIEW_SOURCE_TYPE_OPTIONS,
    smsEngineRunsSearchSchema,
    STATUS_OPTIONS,
    TALENT_SIGNAL_FILTER_OPTIONS,
} from '@apps/web/routes/_app/admin/sms-engine-runs/search_params';
import { useSmsEngineRunsQueryParams } from '@apps/web/routes/_app/admin/sms-engine-runs/use_sms_engine_runs_query_params';
import {
    useSmsEngineRuns,
    useSmsEngineRunStats,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type InlineReviewOutcome,
    type SmsEngineAnnotationSourceType,
    type SmsEngineRunListItem,
    type SmsEngineRunStatus,
} from '@libraries/typescript/api_client';

type SmsEngineRunDomainType = (typeof DOMAIN_TYPE_OPTIONS)[number];
type SmsEngineRunActionType = (typeof ACTION_TYPE_OPTIONS)[number];
type SmsEngineRunMode = (typeof MODE_OPTIONS)[number];
type SmsEngineRunInlineReviewVerdict =
    (typeof INLINE_REVIEW_VERDICT_OPTIONS)[number];
type SmsEngineRunInlineReviewMode = (typeof INLINE_REVIEW_MODE_OPTIONS)[number];
type SmsEngineRunReviewSourceType = (typeof REVIEW_SOURCE_TYPE_OPTIONS)[number];
type SortBy = 'created_at' | 'organization_name' | 'status';
type SortOrder = 'asc' | 'desc';

type DateRange = {
    readonly start: string | undefined;
    readonly end: string | undefined;
};

const INLINE_REVIEW_STATS_LOOKBACK_MS = 24 * 60 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;
const SMS_ENGINE_LOADING_LABEL =
    'im slow at searching things…please be patient 🥹';

type FacetConfig = {
    readonly columnId: string;
    readonly title: string;
    readonly icon?: ComponentType<{ className?: string }>;
    readonly valueFormatter?: (val: unknown) => string;
    readonly renderValue?: (val: unknown) => ReactNode;
    readonly limit?: number;
    readonly valueKind?: 'array' | 'single';
    readonly predefinedOptions?: ReadonlyArray<unknown>;
};

const STATUS_LABELS: Record<SmsEngineRunStatus, string> = {
    unreviewed: 'Unreviewed',
    needs_review: 'Needs review',
    good: 'Good',
    bad: 'Bad',
};

const DOMAIN_TYPE_LABELS: Record<(typeof DOMAIN_TYPE_OPTIONS)[number], string> =
    {
        shift_confirmation: 'Shift confirmation',
        callout: 'Open Shift',
        shifts: 'Shifts',
    };

const MODE_LABELS: Record<SmsEngineRunMode, string> = {
    live: 'Live',
    sandbox: 'Sandbox',
};

const ACTION_TYPE_LABELS: Record<(typeof ACTION_TYPE_OPTIONS)[number], string> =
    {
        shift_confirmation_response: 'Shift confirmation response',
        callout_response: 'Open Shift response',
        clock_in: 'Clock in',
        clock_out: 'Clock out',
        shift_cancellation: 'Shift cancellation',
        shift_cancellation_clarification: 'Shift cancellation clarification',
        shift_cancellation_confirmation_request:
            'Shift cancellation confirmation request',
        shift_cancellation_handoff: 'Shift cancellation handoff',
        no_response_needed: 'No response needed',
        agency_escalation: 'Agency escalation',
        caregiver_availability: 'Caregiver availability',
        caregiver_memory: 'Caregiver memory',
    };

const REVIEW_SOURCE_TYPE_LABELS: Record<SmsEngineRunReviewSourceType, string> =
    {
        human: 'Human',
        user: 'User feedback',
        agent: 'Agent',
    };

const INLINE_REVIEW_OUTCOME_LABELS: Record<InlineReviewOutcome, string> = {
    pending: 'Pending',
    accepted: 'Accepted',
    rewrite_applied: 'Rewrite applied',
    rewrite_no_op: 'Rewrite no-op',
    rewrite_failed: 'Rewrite failed',
    rewrite_skipped: 'Rewrite skipped',
    preview_accepted: 'Preview accepted',
    preview_would_rewrite: 'Preview would rewrite',
    preview_rewrite_failed: 'Preview rewrite failed',
    preview_rewrite_no_op: 'Preview rewrite no-op',
    preview_would_disable_auto_send: 'Preview would disable auto-send',
    error: 'Error',
};

const INLINE_REVIEW_VERDICT_LABELS: Record<
    SmsEngineRunInlineReviewVerdict,
    string
> = {
    GOOD: 'Good',
    BAD: 'Bad',
};

const INLINE_REVIEW_MODE_LABELS: Record<SmsEngineRunInlineReviewMode, string> =
    {
        enforce: 'Enforce',
        preview: 'Preview',
    };

function inlineReviewOutcomeBadgeLabel(run: SmsEngineRunListItem) {
    const outcome = run.inline_review_outcome;
    if (!outcome) {
        return null;
    }
    if (outcome === 'error') {
        return run.inline_review_timeout ? 'Timeout' : 'Inline error';
    }
    return INLINE_REVIEW_OUTCOME_LABELS[outcome] ?? outcome;
}

function inlineReviewOutcomeBadgeClassName(run: SmsEngineRunListItem) {
    if (run.inline_review_outcome !== 'error') {
        return undefined;
    }
    return run.inline_review_timeout
        ? 'bg-amber-100 text-amber-900'
        : 'bg-red-100 text-red-800';
}

export const Route = createFileRoute('/_app/admin/sms-engine-runs/')({
    head: () => ({
        meta: [
            {
                title: 'SMS Engine Runs | Phoebe Admin',
            },
        ],
    }),
    component: SmsEngineRunsPage,
    validateSearch: smsEngineRunsSearchSchema,
});

function toDateInputValue(value: string | undefined) {
    if (!value) {
        return '';
    }
    return value.includes('T') ? value.slice(0, 10) : value;
}

function toStartOfDayIso(value: string | undefined) {
    if (!value) {
        return undefined;
    }
    if (value.includes('T')) {
        return new Date(value).toISOString();
    }
    return new Date(`${value}T00:00:00`).toISOString();
}

function toEndOfDayIso(value: string | undefined) {
    if (!value) {
        return undefined;
    }
    if (value.includes('T')) {
        return new Date(value).toISOString();
    }
    return new Date(`${value}T23:59:59.999`).toISOString();
}

function formatDateLabel(value: string | undefined) {
    if (!value) {
        return '';
    }
    const date = new Date(value.includes('T') ? value : `${value}T00:00:00`);
    return format(date, 'MMM d, yyyy');
}

function SmsEngineRunsPage() {
    const router = useRouter();
    const search = Route.useSearch();
    const {
        page,
        status,
        status_not,
        mode,
        organization_name,
        domain_type,
        action_type,
        inline_review_mode,
        inline_review_verdict,
        inline_review_outcome,
        review_source_type,
        talent_signal,
        has_shadow,
        created_after,
        created_before,
        search: searchQuery,
        sort_by,
        sort_order,
    } = search;

    const routeSearchValue = searchQuery ?? '';
    const createdAfter = created_after;
    const createdBefore = created_before;
    const hasExplicitDateFilter = Boolean(createdAfter || createdBefore);
    const [searchDraft, setSearchDraft] = useState(routeSearchValue);
    const debouncedSearchDraft = useDebouncedValue(
        searchDraft,
        SEARCH_DEBOUNCE_MS,
    );
    const searchDraftEditedRef = useRef(false);
    const [inlineReviewStatsNowMs, setInlineReviewStatsNowMs] = useState(() =>
        Date.now(),
    );

    useEffect(() => {
        searchDraftEditedRef.current = false;
        setSearchDraft(routeSearchValue);
    }, [routeSearchValue]);

    useEffect(() => {
        if (!searchDraftEditedRef.current) {
            return;
        }
        const nextSearch = searchDraft.trim()
            ? debouncedSearchDraft.trim() || undefined
            : undefined;
        if ((routeSearchValue.trim() || undefined) === nextSearch) {
            return;
        }
        router.navigate({
            to: '/admin/sms-engine-runs',
            search: {
                ...search,
                page: 1,
                search: nextSearch,
            },
        });
    }, [debouncedSearchDraft, routeSearchValue, router, search, searchDraft]);

    useEffect(() => {
        const updateInlineReviewStatsWindow = () => {
            setInlineReviewStatsNowMs(Date.now());
        };
        updateInlineReviewStatsWindow();

        const intervalId = window.setInterval(
            updateInlineReviewStatsWindow,
            60_000,
        );
        return () => window.clearInterval(intervalId);
    }, []);

    const rollingInlineReviewStatsCreatedAfter = new Date(
        inlineReviewStatsNowMs - INLINE_REVIEW_STATS_LOOKBACK_MS,
    ).toISOString();
    const getRollingInlineReviewStatsCreatedAfter = useCallback(
        () =>
            new Date(
                Date.now() - INLINE_REVIEW_STATS_LOOKBACK_MS,
            ).toISOString(),
        [],
    );
    const inlineReviewStatsCreatedAfter = hasExplicitDateFilter
        ? toStartOfDayIso(createdAfter)
        : rollingInlineReviewStatsCreatedAfter;

    const { runsQueryParams, orgFilterPending, organizations } =
        useSmsEngineRunsQueryParams(search);

    const normalizedStatus = normalizeValues(status);
    const normalizedStatusNot = normalizeValues(status_not);
    const normalizedMode = normalizeValues(mode);
    const normalizedDomainType = normalizeValues(domain_type);
    const normalizedActionType = normalizeValues(action_type);
    const normalizedInlineReviewVerdict = normalizeValues(
        inline_review_verdict,
    );
    const normalizedInlineReviewMode = normalizeValues(inline_review_mode);
    const normalizedInlineReviewOutcome = normalizeValues(
        inline_review_outcome,
    );
    const normalizedReviewSourceType = normalizeValues(review_source_type);
    const normalizedOrganizationName = normalizeValues(organization_name);
    const normalizedTalentSignal =
        talent_signal === true
            ? true
            : talent_signal === false
              ? false
              : undefined;

    const organizationNameToId = useMemo(() => {
        const map = new Map<string, string>();
        for (const org of organizations ?? []) {
            const displayName = org.internal_name?.trim() || org.name;
            map.set(displayName, org.id);
        }
        return map;
    }, [organizations]);

    const resolvedOrganizationIds = useMemo(() => {
        if (!normalizedOrganizationName?.length) {
            return undefined;
        }
        const ids = normalizedOrganizationName
            .map((name) => organizationNameToId.get(name))
            .filter((id): id is string => id !== null && id !== undefined);
        return ids.length > 0 ? ids : undefined;
    }, [normalizedOrganizationName, organizationNameToId]);

    // Resolve "is not" excludes to complement for the API (which only supports IN filtering)
    const effectiveStatus = useMemo(() => {
        if (normalizedStatusNot?.length) {
            const excludeSet = new Set(normalizedStatusNot);
            return STATUS_OPTIONS.filter((s) => !excludeSet.has(s));
        }
        return normalizedStatus;
    }, [normalizedStatus, normalizedStatusNot]);

    const statsQueryParams = useMemo(
        () => ({
            organization_id: resolvedOrganizationIds,
            status: effectiveStatus,
            mode: normalizedMode,
            domain_type: normalizedDomainType,
            action_type: normalizedActionType,
            inline_review_mode: normalizedInlineReviewMode,
            inline_review_verdict: normalizedInlineReviewVerdict,
            inline_review_outcome: normalizedInlineReviewOutcome,
            review_source_type: normalizedReviewSourceType,
            talent_signal: normalizedTalentSignal,
            has_shadow,
            created_after: toStartOfDayIso(createdAfter),
            created_before: toEndOfDayIso(createdBefore),
            search: searchQuery?.trim() || undefined,
        }),
        [
            resolvedOrganizationIds,
            effectiveStatus,
            normalizedMode,
            normalizedDomainType,
            normalizedActionType,
            normalizedInlineReviewMode,
            normalizedInlineReviewVerdict,
            normalizedInlineReviewOutcome,
            normalizedReviewSourceType,
            normalizedTalentSignal,
            has_shadow,
            createdAfter,
            createdBefore,
            searchQuery,
        ],
    );

    const inlineReviewStatsQueryParams = useMemo(
        () =>
            buildInlineReviewStatsQueryParams(
                statsQueryParams,
                inlineReviewStatsCreatedAfter,
            ),
        [statsQueryParams, inlineReviewStatsCreatedAfter],
    );
    const inlineReviewTimeoutStatsQueryParams = useMemo(
        () => ({
            ...statsQueryParams,
            // Keep this as a rolling 24h pulse even when the table has a
            // custom date range or non-error inline outcome filter.
            created_after: rollingInlineReviewStatsCreatedAfter,
            created_before: undefined,
            inline_review_outcome: undefined,
        }),
        [statsQueryParams, rollingInlineReviewStatsCreatedAfter],
    );

    const {
        data: runsData,
        isLoading,
        isValidating,
        mutate: mutateRuns,
    } = useSmsEngineRuns(runsQueryParams, {
        enabled: !orgFilterPending,
    });

    const { data: stats, mutate: mutateStats } = useSmsEngineRunStats(
        statsQueryParams,
        { enabled: !orgFilterPending },
    );
    const { data: inlineReviewStats, mutate: mutateInlineReviewStats } =
        useSmsEngineRunStats(inlineReviewStatsQueryParams, {
            enabled: !orgFilterPending,
        });
    const {
        data: inlineReviewTimeoutStats,
        mutate: mutateInlineReviewTimeoutStats,
    } = useSmsEngineRunStats(inlineReviewTimeoutStatsQueryParams, {
        enabled: !orgFilterPending,
    });

    const items = useMemo(() => runsData?.items ?? [], [runsData?.items]);
    const total = runsData?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

    useEffect(() => {
        if (!isLoading && page > totalPages) {
            router.navigate({
                to: '/admin/sms-engine-runs',
                search: {
                    ...search,
                    page: totalPages,
                },
                replace: true,
            });
        }
    }, [isLoading, page, totalPages, router, search]);

    const organizationOptions = useMemo(() => {
        return (organizations ?? [])
            .map((org) => ({
                id: org.internal_name?.trim() || org.name,
                name: org.internal_name?.trim() || org.name,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [organizations]);

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [dateRangeDraft, setDateRangeDraft] = useState<DateRange>({
        start: createdAfter,
        end: createdBefore,
    });

    useEffect(() => {
        setSelectedIds(new Set());
    }, [page, items]);

    useEffect(() => {
        setDateRangeDraft({
            start: createdAfter,
            end: createdBefore,
        });
    }, [createdAfter, createdBefore]);

    const selectedCount = useMemo(
        () =>
            items.reduce(
                (count, run) => count + (selectedIds.has(run.id) ? 1 : 0),
                0,
            ),
        [items, selectedIds],
    );
    const allSelected = items.length > 0 && selectedCount === items.length;
    const someSelected = selectedCount > 0 && !allSelected;

    const toggleSelectAll = useCallback(
        (checked: boolean) => {
            setSelectedIds((current) => {
                const next = new Set(current);
                if (checked) {
                    items.forEach((run) => next.add(run.id));
                } else {
                    items.forEach((run) => next.delete(run.id));
                }
                return next;
            });
        },
        [items],
    );

    const toggleRow = useCallback((runId: string, checked: boolean) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (checked) {
                next.add(runId);
            } else {
                next.delete(runId);
            }
            return next;
        });
    }, []);

    const handleSearchChange = useCallback(
        (value: string) => {
            searchDraftEditedRef.current = true;
            setSearchDraft(value);
            if (value.trim()) {
                return;
            }
            router.navigate({
                to: '/admin/sms-engine-runs',
                search: {
                    ...search,
                    page: 1,
                    search: value.trim() ? value : undefined,
                },
            });
        },
        [router, search],
    );

    const handleColumnFiltersChange = useCallback(
        (nextFilters: ColumnFiltersState) => {
            const nextOrganizationName = normalizeValues(
                extractFilterValues<string>(nextFilters, 'organization_name'),
            );
            const nextStatus = normalizeValues(
                extractFilterValues<SmsEngineRunStatus>(nextFilters, 'status'),
            );
            const nextStatusNot = normalizeValues(
                extractExcludeFilterValues<SmsEngineRunStatus>(
                    nextFilters,
                    'status',
                ),
            );
            const nextMode = normalizeValues(
                extractFilterValues<SmsEngineRunMode>(nextFilters, 'mode'),
            );
            const nextDomainType = normalizeValues(
                extractFilterValues<SmsEngineRunDomainType>(
                    nextFilters,
                    'domain_type',
                ),
            );
            const nextActionType = normalizeValues(
                extractFilterValues<SmsEngineRunActionType>(
                    nextFilters,
                    'action_types',
                ),
            );
            const nextInlineReviewVerdict = normalizeValues(
                extractFilterValues<SmsEngineRunInlineReviewVerdict>(
                    nextFilters,
                    'inline_review_verdict',
                ),
            );
            const nextInlineReviewMode = normalizeValues(
                extractFilterValues<SmsEngineRunInlineReviewMode>(
                    nextFilters,
                    'inline_review_mode',
                ),
            );
            const nextInlineReviewOutcome = normalizeValues(
                extractFilterValues<InlineReviewOutcome>(
                    nextFilters,
                    'inline_review_outcome',
                ),
            );
            const nextReviewSourceType = normalizeValues(
                extractFilterValues<SmsEngineRunReviewSourceType>(
                    nextFilters,
                    'review_source_type',
                ),
            );
            const nextTalentSignal = extractBooleanColumnFilter(
                nextFilters,
                'talent_signal',
            );
            const nextHasShadow = extractBooleanColumnFilter(
                nextFilters,
                'has_shadow',
            );

            if (
                areArraysEqual(
                    normalizedOrganizationName,
                    nextOrganizationName,
                ) &&
                areArraysEqual(normalizedStatus, nextStatus) &&
                areArraysEqual(normalizedStatusNot, nextStatusNot) &&
                areArraysEqual(normalizedMode, nextMode) &&
                areArraysEqual(normalizedDomainType, nextDomainType) &&
                areArraysEqual(normalizedActionType, nextActionType) &&
                areArraysEqual(
                    normalizedInlineReviewVerdict,
                    nextInlineReviewVerdict,
                ) &&
                areArraysEqual(
                    normalizedInlineReviewMode,
                    nextInlineReviewMode,
                ) &&
                areArraysEqual(
                    normalizedInlineReviewOutcome,
                    nextInlineReviewOutcome,
                ) &&
                areArraysEqual(
                    normalizedReviewSourceType,
                    nextReviewSourceType,
                ) &&
                normalizedTalentSignal === nextTalentSignal &&
                has_shadow === nextHasShadow
            ) {
                return;
            }

            router.navigate({
                to: '/admin/sms-engine-runs',
                search: {
                    ...search,
                    page: 1,
                    organization_name: nextOrganizationName,
                    status: nextStatus,
                    status_not: nextStatusNot,
                    mode: nextMode,
                    domain_type: nextDomainType,
                    action_type: nextActionType,
                    inline_review_mode: nextInlineReviewMode,
                    inline_review_verdict: nextInlineReviewVerdict,
                    inline_review_outcome: nextInlineReviewOutcome,
                    review_source_type: nextReviewSourceType,
                    talent_signal: nextTalentSignal,
                    has_shadow: nextHasShadow,
                },
            });
        },
        [
            router,
            search,
            normalizedOrganizationName,
            normalizedStatus,
            normalizedStatusNot,
            normalizedMode,
            normalizedDomainType,
            normalizedActionType,
            normalizedInlineReviewMode,
            normalizedInlineReviewVerdict,
            normalizedInlineReviewOutcome,
            normalizedReviewSourceType,
            normalizedTalentSignal,
            has_shadow,
        ],
    );

    const handleDateRangeApply = useCallback(
        (nextRange: DateRange) => {
            const {
                created_after: _ca,
                created_before: _cb,
                ...restSearch
            } = search;
            router.navigate({
                to: '/admin/sms-engine-runs',
                search: {
                    ...restSearch,
                    page: 1,
                    ...(nextRange.start && { created_after: nextRange.start }),
                    ...(nextRange.end && { created_before: nextRange.end }),
                },
            });
        },
        [router, search],
    );

    const handleSortChange = useCallback(
        (nextSortBy: SortBy) => {
            const isSameColumn = sort_by === nextSortBy;
            const nextOrder: SortOrder = isSameColumn
                ? sort_order === 'asc'
                    ? 'desc'
                    : 'asc'
                : 'desc';
            router.navigate({
                to: '/admin/sms-engine-runs',
                search: {
                    ...search,
                    page: 1,
                    sort_by: nextSortBy,
                    sort_order: nextOrder,
                },
            });
        },
        [router, search, sort_by, sort_order],
    );

    const handleBulkUpdate = useCallback(
        async (nextStatus: SmsEngineRunStatus) => {
            if (selectedIds.size === 0) {
                return;
            }
            setIsBulkUpdating(true);
            try {
                await getApiClient().post({
                    url: '/admin/sms-engine-runs/bulk-status',
                    body: {
                        run_ids: Array.from(selectedIds),
                        status: nextStatus,
                    },
                });
                setSelectedIds(new Set());
                await Promise.all([
                    mutateRuns(),
                    mutateStats(),
                    mutateInlineReviewStats(),
                    mutateInlineReviewTimeoutStats(),
                ]);
            } finally {
                setIsBulkUpdating(false);
            }
        },
        [
            selectedIds,
            mutateRuns,
            mutateStats,
            mutateInlineReviewStats,
            mutateInlineReviewTimeoutStats,
        ],
    );

    const handleInlineReviewVerdictClick = useCallback(
        (verdict: SmsEngineRunInlineReviewVerdict) => {
            router.navigate({
                to: '/admin/sms-engine-runs',
                search: {
                    ...search,
                    page: 1,
                    inline_review_verdict: [verdict],
                    created_after: hasExplicitDateFilter
                        ? createdAfter
                        : getRollingInlineReviewStatsCreatedAfter(),
                },
            });
        },
        [
            router,
            search,
            createdAfter,
            hasExplicitDateFilter,
            getRollingInlineReviewStatsCreatedAfter,
        ],
    );

    const handleInlineReviewOutcomeClick = useCallback(
        (outcome: InlineReviewOutcome) => {
            router.navigate({
                to: '/admin/sms-engine-runs',
                search: {
                    ...search,
                    page: 1,
                    inline_review_outcome: [outcome],
                    created_after: hasExplicitDateFilter
                        ? createdAfter
                        : getRollingInlineReviewStatsCreatedAfter(),
                },
            });
        },
        [
            router,
            search,
            createdAfter,
            hasExplicitDateFilter,
            getRollingInlineReviewStatsCreatedAfter,
        ],
    );

    const handleClearFilters = useCallback(() => {
        router.navigate({
            to: '/admin/sms-engine-runs',
            search: () => ({
                page: 1,
                sort_by: 'created_at' as const,
                sort_order: 'desc' as const,
            }),
        });
    }, [router]);

    const columnFiltersFromSearch = useMemo(() => {
        const filters: ColumnFiltersState = [];
        if (normalizedOrganizationName?.length) {
            filters.push({
                id: 'organization_name',
                value: normalizedOrganizationName,
            });
        }
        if (normalizedStatus?.length || normalizedStatusNot?.length) {
            filters.push({
                id: 'status',
                value: normalizedStatusNot?.length
                    ? {
                          include: normalizedStatus ?? [],
                          exclude: normalizedStatusNot,
                      }
                    : normalizedStatus,
            });
        }
        if (normalizedMode?.length) {
            filters.push({ id: 'mode', value: normalizedMode });
        }
        if (normalizedDomainType?.length) {
            filters.push({ id: 'domain_type', value: normalizedDomainType });
        }
        if (normalizedActionType?.length) {
            filters.push({ id: 'action_types', value: normalizedActionType });
        }
        if (normalizedInlineReviewVerdict?.length) {
            filters.push({
                id: 'inline_review_verdict',
                value: normalizedInlineReviewVerdict,
            });
        }
        if (normalizedInlineReviewMode?.length) {
            filters.push({
                id: 'inline_review_mode',
                value: normalizedInlineReviewMode,
            });
        }
        if (normalizedInlineReviewOutcome?.length) {
            filters.push({
                id: 'inline_review_outcome',
                value: normalizedInlineReviewOutcome,
            });
        }
        if (normalizedReviewSourceType?.length) {
            filters.push({
                id: 'review_source_type',
                value: normalizedReviewSourceType,
            });
        }
        if (normalizedTalentSignal !== undefined) {
            filters.push({
                id: 'talent_signal',
                value: booleanColumnFilterValue(normalizedTalentSignal),
            });
        }
        if (has_shadow !== undefined) {
            filters.push({
                id: 'has_shadow',
                value: booleanColumnFilterValue(has_shadow),
            });
        }
        return filters;
    }, [
        normalizedOrganizationName,
        normalizedStatus,
        normalizedStatusNot,
        normalizedMode,
        normalizedDomainType,
        normalizedActionType,
        normalizedInlineReviewVerdict,
        normalizedInlineReviewMode,
        normalizedInlineReviewOutcome,
        normalizedReviewSourceType,
        normalizedTalentSignal,
        has_shadow,
    ]);

    const facetedFilters = useMemo<Array<FacetConfig>>(
        () => [
            {
                columnId: 'organization_name',
                title: 'Organization',
                icon: Building,
                predefinedOptions: organizationOptions.map((org) => org.id),
                valueFormatter: (value: unknown) => {
                    const org = organizationOptions.find((o) => o.id === value);
                    return org?.name ?? String(value);
                },
            },
            {
                columnId: 'status',
                title: 'Assessment',
                predefinedOptions: STATUS_OPTIONS,
                valueFormatter: (value: unknown) =>
                    STATUS_LABELS[value as SmsEngineRunStatus] ?? String(value),
            },
            {
                columnId: 'mode',
                title: 'Mode',
                predefinedOptions: MODE_OPTIONS,
                valueFormatter: (value: unknown) =>
                    MODE_LABELS[value as SmsEngineRunMode] ?? String(value),
            },
            {
                columnId: 'domain_type',
                title: 'Domain type',
                predefinedOptions: DOMAIN_TYPE_OPTIONS,
                valueFormatter: (value: unknown) =>
                    DOMAIN_TYPE_LABELS[
                        value as (typeof DOMAIN_TYPE_OPTIONS)[number]
                    ] ?? String(value),
            },
            {
                columnId: 'action_types',
                title: 'Action type',
                predefinedOptions: ACTION_TYPE_OPTIONS,
                valueFormatter: (value: unknown) =>
                    ACTION_TYPE_LABELS[
                        value as (typeof ACTION_TYPE_OPTIONS)[number]
                    ] ?? String(value),
                valueKind: 'array' as const,
            },
            {
                columnId: 'review_source_type',
                title: 'Review source',
                icon: UserCheck,
                predefinedOptions: REVIEW_SOURCE_TYPE_OPTIONS,
                valueFormatter: (value: unknown) =>
                    REVIEW_SOURCE_TYPE_LABELS[
                        value as SmsEngineRunReviewSourceType
                    ] ?? String(value),
                valueKind: 'array' as const,
            },
            {
                columnId: 'has_shadow',
                title: 'Shadow comparison',
                predefinedOptions: [true],
                valueFormatter: (value: unknown) =>
                    value === true ? 'Shadowed runs' : String(value),
            },
            {
                columnId: 'talent_signal',
                title: 'Signal',
                icon: Sparkles,
                predefinedOptions: TALENT_SIGNAL_FILTER_OPTIONS,
                valueFormatter: (value: unknown) =>
                    value === true ? 'Talent signal' : String(value),
            },
            {
                columnId: 'inline_review_verdict',
                title: 'Inline verdict',
                predefinedOptions: INLINE_REVIEW_VERDICT_OPTIONS,
                valueFormatter: (value: unknown) =>
                    INLINE_REVIEW_VERDICT_LABELS[
                        value as SmsEngineRunInlineReviewVerdict
                    ] ?? String(value),
            },
            {
                columnId: 'inline_review_mode',
                title: 'Inline mode',
                predefinedOptions: INLINE_REVIEW_MODE_OPTIONS,
                valueFormatter: (value: unknown) =>
                    INLINE_REVIEW_MODE_LABELS[
                        value as SmsEngineRunInlineReviewMode
                    ] ?? String(value),
            },
            {
                columnId: 'inline_review_outcome',
                title: 'Inline review',
                predefinedOptions: INLINE_REVIEW_OUTCOME_OPTIONS,
                valueFormatter: (value: unknown) =>
                    INLINE_REVIEW_OUTCOME_LABELS[
                        value as InlineReviewOutcome
                    ] ?? String(value),
            },
        ],
        [organizationOptions],
    );

    const dateFilterActive = hasExplicitDateFilter;
    const dateFilterLabel = useMemo(() => {
        if (!dateFilterActive) {
            return undefined;
        }
        const startLabel = formatDateLabel(createdAfter);
        const endLabel = formatDateLabel(createdBefore);
        if (startLabel && endLabel) {
            return `${startLabel} → ${endLabel}`;
        }
        if (startLabel) {
            return `From ${startLabel}`;
        }
        if (endLabel) {
            return `Through ${endLabel}`;
        }
        return undefined;
    }, [createdAfter, createdBefore, dateFilterActive]);
    const statsWindowSuffix = hasExplicitDateFilter ? '' : ' (24h)';

    const customFilters = useMemo<Array<CustomFilterConfig>>(
        () => [
            {
                id: 'created_at',
                title: 'Date range',
                icon: CalendarDays,
                selectedCount: dateFilterActive ? 1 : 0,
                activeFilterLabels: dateFilterLabel ? [dateFilterLabel] : [],
                onOpen: () => {
                    setDateRangeDraft({
                        start: createdAfter,
                        end: createdBefore,
                    });
                },
                onClear: dateFilterActive
                    ? () =>
                          handleDateRangeApply({
                              start: undefined,
                              end: undefined,
                          })
                    : undefined,
                children: (
                    <div className="flex flex-col gap-3 p-2">
                        <div className="space-y-1">
                            <Label className="text-muted-foreground text-xs">
                                Start date
                            </Label>
                            <Input
                                type="date"
                                value={toDateInputValue(dateRangeDraft.start)}
                                onChange={(event) =>
                                    setDateRangeDraft((current) => ({
                                        ...current,
                                        start: event.target.value || undefined,
                                    }))
                                }
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-muted-foreground text-xs">
                                End date
                            </Label>
                            <Input
                                type="date"
                                value={toDateInputValue(dateRangeDraft.end)}
                                onChange={(event) =>
                                    setDateRangeDraft((current) => ({
                                        ...current,
                                        end: event.target.value || undefined,
                                    }))
                                }
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() =>
                                    handleDateRangeApply(dateRangeDraft)
                                }
                            >
                                Apply
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() =>
                                    handleDateRangeApply({
                                        start: undefined,
                                        end: undefined,
                                    })
                                }
                            >
                                Clear
                            </Button>
                        </div>
                    </div>
                ),
            },
        ],
        [
            dateFilterActive,
            dateFilterLabel,
            createdAfter,
            createdBefore,
            dateRangeDraft,
            handleDateRangeApply,
        ],
    );

    const columns = useMemo<Array<ColumnDef<SmsEngineRunListItem>>>(
        () => [
            {
                id: 'select',
                header: () => (
                    <div
                        className="flex items-center justify-center"
                        data-row-click-ignore={true}
                    >
                        <Checkbox
                            aria-label="Select all runs"
                            checked={
                                allSelected
                                    ? true
                                    : someSelected
                                      ? 'indeterminate'
                                      : false
                            }
                            onCheckedChange={(value) =>
                                toggleSelectAll(value === true)
                            }
                        />
                    </div>
                ),
                cell: ({ row }) => (
                    <div
                        className="flex items-center justify-center"
                        data-row-click-ignore={true}
                    >
                        <Checkbox
                            aria-label={`Select run ${row.original.id}`}
                            checked={selectedIds.has(row.original.id)}
                            onCheckedChange={(value) =>
                                toggleRow(row.original.id, value === true)
                            }
                        />
                    </div>
                ),
                size: 44,
                enableSorting: false,
                enableHiding: false,
            },
            {
                accessorKey: 'created_at',
                header: () => (
                    <SortableHeader
                        label="Created"
                        sortKey="created_at"
                        sortBy={sort_by}
                        sortOrder={sort_order}
                        onSort={handleSortChange}
                    />
                ),
                cell: ({ row }) => (
                    <span className="text-muted-foreground text-sm">
                        {formatRelativeTimeShort(
                            new Date(row.original.created_at),
                        )}
                    </span>
                ),
                size: 140,
            },
            {
                accessorKey: 'status',
                header: () => (
                    <SortableHeader
                        label="Assessment"
                        sortKey="status"
                        sortBy={sort_by}
                        sortOrder={sort_order}
                        onSort={handleSortChange}
                    />
                ),
                cell: ({ row }) => <StatusBadge status={row.original.status} />,
                size: 140,
                filterFn: () => true,
            },
            {
                accessorKey: 'organization_name',
                header: () => (
                    <SortableHeader
                        label="Organization"
                        sortKey="organization_name"
                        sortBy={sort_by}
                        sortOrder={sort_order}
                        onSort={handleSortChange}
                    />
                ),
                cell: ({ row }) => (
                    <span className="font-medium">
                        {row.original.organization_name || '—'}
                    </span>
                ),
                size: 200,
            },
            {
                accessorKey: 'caregiver_name',
                header: 'Caregiver',
                cell: ({ row }) => row.original.caregiver_name || '—',
                size: 180,
                enableSorting: false,
            },
            {
                accessorKey: 'inbound_message',
                header: 'Inbound Message',
                cell: ({ row }) => {
                    const message = row.original.inbound_message;
                    if (!message) {
                        return <span className="text-sm">—</span>;
                    }
                    if (message.length <= 50) {
                        return <span className="text-sm">{message}</span>;
                    }
                    return (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <span className="text-sm">
                                    {message.slice(0, 50)}…
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-sm">
                                {message}
                            </TooltipContent>
                        </Tooltip>
                    );
                },
                size: 260,
                enableSorting: false,
            },
            {
                accessorKey: 'final_reply',
                header: 'Reply',
                cell: ({ row }) => {
                    const reply = row.original.final_reply;
                    if (!reply) {
                        return <span className="text-sm">—</span>;
                    }
                    if (reply.length <= 50) {
                        return <span className="text-sm">{reply}</span>;
                    }
                    return (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <span className="text-sm">
                                    {reply.slice(0, 50)}…
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-sm">
                                {reply}
                            </TooltipContent>
                        </Tooltip>
                    );
                },
                size: 260,
                enableSorting: false,
            },
            {
                accessorKey: 'actions_summary',
                header: 'Actions',
                cell: ({ row }) => (
                    <div className="flex flex-wrap gap-1">
                        {row.original.actions_summary.length > 0 ? (
                            row.original.actions_summary.map((action, idx) => (
                                <Badge
                                    key={`${row.original.id}-${idx}`}
                                    variant="secondary"
                                    className="text-xs"
                                >
                                    {action}
                                </Badge>
                            ))
                        ) : (
                            <span className="text-muted-foreground text-sm">
                                —
                            </span>
                        )}
                    </div>
                ),
                enableSorting: false,
            },
            {
                accessorKey: 'mode',
                header: 'Mode',
                cell: ({ row }) => (
                    <Badge variant="outline">
                        {MODE_LABELS[row.original.mode] ?? row.original.mode}
                    </Badge>
                ),
                enableSorting: false,
                filterFn: () => true,
            },
            {
                accessorKey: 'domain_type',
                header: 'Domain',
                enableSorting: false,
                filterFn: () => true,
            },
            {
                accessorKey: 'inline_review_outcome',
                header: 'Inline Review',
                cell: ({ row }) => {
                    const badgeLabel = inlineReviewOutcomeBadgeLabel(
                        row.original,
                    );
                    if (!badgeLabel) {
                        return (
                            <span className="text-muted-foreground text-sm">
                                —
                            </span>
                        );
                    }
                    return (
                        <Badge
                            variant="secondary"
                            className={inlineReviewOutcomeBadgeClassName(
                                row.original,
                            )}
                        >
                            {badgeLabel}
                        </Badge>
                    );
                },
                enableSorting: false,
                filterFn: () => true,
            },
            {
                accessorKey: 'inline_review_mode',
                header: 'Inline Mode',
                cell: ({ row }) => {
                    const mode = row.original.inline_review_mode;
                    if (!mode) {
                        return (
                            <span className="text-muted-foreground text-sm">
                                —
                            </span>
                        );
                    }
                    return (
                        <Badge variant="outline">
                            {INLINE_REVIEW_MODE_LABELS[
                                mode as SmsEngineRunInlineReviewMode
                            ] ?? mode}
                        </Badge>
                    );
                },
                enableSorting: false,
                filterFn: () => true,
            },
            {
                accessorKey: 'inline_review_verdict',
                header: 'Inline Verdict',
                cell: ({ row }) => {
                    const verdict = row.original.inline_review_verdict;
                    if (!verdict) {
                        return (
                            <span className="text-muted-foreground text-sm">
                                —
                            </span>
                        );
                    }
                    return (
                        <Badge
                            className={
                                verdict === 'GOOD'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                            }
                        >
                            {INLINE_REVIEW_VERDICT_LABELS[
                                verdict as SmsEngineRunInlineReviewVerdict
                            ] ?? verdict}
                        </Badge>
                    );
                },
                enableSorting: false,
                filterFn: () => true,
            },
            {
                id: 'has_shadow',
                accessorFn: () => null,
                header: 'Shadow comparison',
                enableHiding: false,
                enableSorting: false,
                filterFn: () => true,
            },
            {
                accessorKey: 'talent_signal',
                header: 'Signals',
                cell: ({ row }) =>
                    row.original.talent_signal ? (
                        <Badge
                            variant="secondary"
                            className="bg-amber-100 text-amber-900"
                        >
                            Talent signal
                        </Badge>
                    ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                    ),
                enableSorting: false,
                filterFn: () => true,
            },
            {
                id: 'review_source_type',
                accessorFn: (run) => run.review_source_types,
                header: 'Review Source',
                cell: ({ row }) => (
                    <div className="flex flex-wrap gap-1">
                        {row.original.review_source_types.length > 0 ? (
                            row.original.review_source_types.map((source) => (
                                <Badge
                                    key={`${row.original.id}-${source}`}
                                    variant="secondary"
                                    className="text-xs"
                                >
                                    {REVIEW_SOURCE_TYPE_LABELS[
                                        source as SmsEngineAnnotationSourceType
                                    ] ?? source}
                                </Badge>
                            ))
                        ) : (
                            <span className="text-muted-foreground text-sm">
                                —
                            </span>
                        )}
                    </div>
                ),
                enableSorting: false,
                filterFn: () => true,
            },
            {
                accessorKey: 'action_types',
                header: 'Action Types',
                enableSorting: false,
                filterFn: () => true,
            },
        ],
        [
            allSelected,
            someSelected,
            selectedIds,
            toggleSelectAll,
            toggleRow,
            sort_by,
            sort_order,
            handleSortChange,
        ],
    );

    return (
        <div className="flex h-full flex-col">
            <div className="border-border border-b p-4">
                <h1 className="text-xl font-semibold">SMS Engine Runs</h1>
                <p className="text-muted-foreground mt-1 text-sm">
                    Review SMS engine run assessments and inline review outcomes
                </p>
            </div>
            {stats && (
                <div className="border-border grid grid-cols-1 gap-4 border-b p-4 md:grid-cols-2 xl:grid-cols-6">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-muted-foreground text-sm font-medium">
                                Total Runs
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {stats.total_runs.toLocaleString()}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-muted-foreground text-sm font-medium">
                                Inline Timeouts (24h)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-amber-700">
                                {inlineReviewTimeoutStats
                                    ? inlineReviewTimeoutStats.inline_review_timeout_count.toLocaleString()
                                    : '—'}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-muted-foreground text-sm font-medium">
                                {`Inline Errors${statsWindowSuffix}`}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {inlineReviewStats ? (
                                    <button
                                        type="button"
                                        className="focus-visible:ring-ring rounded-sm text-red-600 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                                        onClick={() =>
                                            handleInlineReviewOutcomeClick(
                                                'error',
                                            )
                                        }
                                    >
                                        {inlineReviewStats.inline_review_error_count.toLocaleString()}
                                    </button>
                                ) : (
                                    <span className="text-muted-foreground">
                                        —
                                    </span>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-muted-foreground text-sm font-medium">
                                {`Inline Reviewed${statsWindowSuffix}`}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {inlineReviewStats
                                    ? inlineReviewStats.reviewed_count.toLocaleString()
                                    : '—'}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-muted-foreground text-sm font-medium">
                                {`Inline Good / Bad${statsWindowSuffix}`}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-2 text-2xl font-bold">
                                {inlineReviewStats ? (
                                    <button
                                        type="button"
                                        className="focus-visible:ring-ring rounded-sm text-green-600 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                                        onClick={() =>
                                            handleInlineReviewVerdictClick(
                                                'GOOD',
                                            )
                                        }
                                    >
                                        {inlineReviewStats.good_count}
                                    </button>
                                ) : (
                                    <span className="text-muted-foreground">
                                        —
                                    </span>
                                )}
                                <span className="text-muted-foreground">/</span>
                                {inlineReviewStats ? (
                                    <button
                                        type="button"
                                        className="focus-visible:ring-ring rounded-sm text-red-600 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none"
                                        onClick={() =>
                                            handleInlineReviewVerdictClick(
                                                'BAD',
                                            )
                                        }
                                    >
                                        {inlineReviewStats.bad_count}
                                    </button>
                                ) : (
                                    <span className="text-muted-foreground">
                                        —
                                    </span>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-muted-foreground text-sm font-medium">
                                {`Inline Quality${statsWindowSuffix}`}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">
                                {inlineReviewStats?.quality_score !== null &&
                                inlineReviewStats?.quality_score !== undefined
                                    ? `${(inlineReviewStats.quality_score * 100).toFixed(1)}%`
                                    : '—'}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
            <div className="border-border flex items-center justify-between border-b px-4 py-3">
                <span className="text-muted-foreground text-sm">
                    {total.toLocaleString()} runs
                </span>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            router.navigate({
                                to: '/admin/sms-engine-runs',
                                search: { ...search, page: page - 1 },
                            })
                        }
                        disabled={page <= 1}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                        Page {page} of {totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            router.navigate({
                                to: '/admin/sms-engine-runs',
                                search: { ...search, page: page + 1 },
                            })
                        }
                        disabled={page >= totalPages}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            {selectedCount > 0 && (
                <div className="border-border bg-muted/40 flex flex-wrap items-center justify-between gap-3 border-b px-4 py-2">
                    <span className="text-sm font-medium">
                        {selectedCount} selected
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBulkUpdate('good')}
                            disabled={isBulkUpdating}
                        >
                            {isBulkUpdating ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <ThumbsUp className="mr-2 h-4 w-4" />
                            )}
                            Mark as Good
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBulkUpdate('bad')}
                            disabled={isBulkUpdating}
                        >
                            {isBulkUpdating ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <ThumbsDown className="mr-2 h-4 w-4" />
                            )}
                            Mark as Bad
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBulkUpdate('needs_review')}
                            disabled={isBulkUpdating}
                        >
                            {isBulkUpdating ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <AlertCircle className="mr-2 h-4 w-4" />
                            )}
                            Mark as Needs Review
                        </Button>
                    </div>
                </div>
            )}
            <div className="flex-1 overflow-auto p-4">
                <nav
                    aria-label="SMS engine run views"
                    className="mb-3 flex flex-wrap gap-2"
                >
                    <Button
                        size="sm"
                        variant={
                            has_shadow === undefined ? 'default' : 'outline'
                        }
                        className="rounded-full"
                        aria-pressed={has_shadow === undefined}
                        onClick={() =>
                            router.navigate({
                                to: '/admin/sms-engine-runs',
                                search: {
                                    ...search,
                                    has_shadow: undefined,
                                    page: 1,
                                },
                            })
                        }
                    >
                        All runs
                    </Button>
                    <Button
                        size="sm"
                        variant={has_shadow === true ? 'default' : 'outline'}
                        className="rounded-full"
                        aria-pressed={has_shadow === true}
                        onClick={() =>
                            router.navigate({
                                to: '/admin/sms-engine-runs',
                                search: {
                                    ...search,
                                    has_shadow: true,
                                    page: 1,
                                },
                            })
                        }
                    >
                        Shadowed runs
                    </Button>
                </nav>
                <DataTable
                    columns={columns}
                    data={items}
                    isLoading={isLoading}
                    loadingLabel={SMS_ENGINE_LOADING_LABEL}
                    hidePagination={true}
                    getRowNavigation={(row) => {
                        const routeOptions = {
                            to: '/admin/sms-engine-runs/$runId',
                            params: { runId: row.id },
                            search: { ...search, from_list: true },
                        } as const;
                        return {
                            href: router.buildLocation(routeOptions).href,
                            navigate: () => {
                                void router.navigate(routeOptions);
                            },
                        };
                    }}
                    onColumnFiltersChange={handleColumnFiltersChange}
                    initialColumnVisibility={{
                        has_shadow: false,
                        mode: false,
                        domain_type: false,
                        inline_review_mode: false,
                        review_source_type: false,
                        action_types: false,
                    }}
                    renderToolbar={(table) => (
                        <SmsEngineRunsToolbar
                            table={table}
                            columnFilters={columnFiltersFromSearch}
                            facetedFilters={facetedFilters}
                            customFilters={customFilters}
                            searchValue={searchDraft}
                            onSearchChange={handleSearchChange}
                            isLoading={isValidating}
                        />
                    )}
                    renderEmptyState={() => (
                        <EmptyState onClear={handleClearFilters} />
                    )}
                />
            </div>
        </div>
    );
}

function SmsEngineRunsToolbar<TData extends RowData>({
    table,
    columnFilters,
    facetedFilters,
    customFilters,
    searchValue,
    onSearchChange,
    isLoading,
}: {
    table: TableType<TData>;
    columnFilters: ColumnFiltersState;
    facetedFilters: Array<FacetConfig>;
    customFilters: Array<CustomFilterConfig>;
    searchValue: string;
    onSearchChange: (value: string) => void;
    isLoading: boolean;
}) {
    useEffect(() => {
        const current = table.state.columnFilters;
        if (!areColumnFiltersEqual(current, columnFilters)) {
            table.setColumnFilters(columnFilters);
        }
    }, [table, columnFilters]);

    return (
        <DataTableToolbar
            table={table}
            facetedFilters={facetedFilters}
            customFilters={customFilters}
            globalFilterPlaceholder="Search message, reply, trace ID, or run ID"
            globalFilterValue={searchValue}
            onGlobalFilterChange={onSearchChange}
            serverSideGlobalFilter={true}
            isLoading={isLoading}
        />
    );
}

function SortableHeader({
    label,
    sortKey,
    sortBy,
    sortOrder,
    onSort,
}: {
    label: string;
    sortKey: SortBy;
    sortBy: SortBy;
    sortOrder: SortOrder;
    onSort: (next: SortBy) => void;
}) {
    const isActive = sortBy === sortKey;
    const Icon = isActive
        ? sortOrder === 'asc'
            ? ArrowUp
            : ArrowDown
        : ArrowUpDown;

    return (
        <Button
            variant="ghost"
            size="sm"
            className={cn('h-6 text-xs', isActive && 'text-foreground')}
            onClick={() => onSort(sortKey)}
        >
            <Icon className="mr-1 h-3 w-3" />
            {label}
        </Button>
    );
}

function EmptyState({ onClear }: { onClear: () => void }) {
    return (
        <div className="flex h-64 flex-col items-center justify-center gap-3">
            <div className="text-muted-foreground text-sm">No runs found</div>
            <Button variant="outline" size="sm" onClick={onClear}>
                Clear all filters
            </Button>
        </div>
    );
}

function StatusBadge({ status }: { status: SmsEngineRunStatus }) {
    if (status === 'good') {
        return (
            <Badge className="bg-green-100 text-green-800">
                <ThumbsUp className="mr-1 h-3 w-3" />
                Good
            </Badge>
        );
    }

    if (status === 'unreviewed') {
        return (
            <Badge variant="outline" className="text-muted-foreground">
                Unreviewed
            </Badge>
        );
    }

    if (status === 'bad') {
        return (
            <Badge className="bg-red-100 text-red-800">
                <ThumbsDown className="mr-1 h-3 w-3" />
                Bad
            </Badge>
        );
    }

    return (
        <Badge className="bg-yellow-100 text-yellow-800">
            <AlertCircle className="mr-1 h-3 w-3" />
            Needs review
        </Badge>
    );
}
